import AuthWrapper from 'components/AuthWrapper';
import {
  getCustomBrandingDetail,
  isMobileNumberRequiredForLinking,
  isNewSignupWorkflow,
} from 'helpers/customBranding';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect, withRouter } from 'react-router-dom';
import { signUp, togglePasswordVisibility } from 'redux-modules/auth/actions';
import * as selectors from 'redux-modules/auth/selectors';
import HpaStoreContext from '../../../contexts/HpaStoreContext';
import errorMessages from '../../../helpers/errorMessages';
import getInitialValuesFromQuery from '../../../helpers/getInitialValuesFromQuery';
import {
  WORKFLOW_TYPE_LINK_ACCOUNT,
  normalizeRegistrationWorkflowType,
} from 'helpers/registrationWorkflow';
import VerificationCode from '../../NewRegistrationFlow/Verification';
import ClaimAccount from '../ClaimAccount';
import EmailValidation from '../PreSignUp/EmailValidation';
import Registration from '../Registration';
import Success from '../Success';
import MobilePromptMatchFound from '../MobilePromptMatchFound';
import SelectPrimaryLogin from '../SelectPrimaryLogin';

// Registration step constants
export const registrationSteps = {
  CLAIMACCOUNT: 0,
  EMAILVALIDATION: 1,
  VERIFICATIONCODE: 2,
  REGISTRATION: 3,
  SUCCESS: 4,
  MOBILEPROMPT: 5,
  SELECTPRIMARYLOGIN: 6,
};

const toSignIn = {
  pathname: '/login',
  search: window.location.search,
};

const Register = withRouter(
  ({
    isRequestPending,
    PasswordComplexityFeature,
    user,
    history,
    location,
    workflowType,
  }) => {
    const [registrationStep, setRegistrationStep] = useState(1);
    const { username } = useContext(HpaStoreContext);
    const context = useContext(HpaStoreContext);
    let initialValues = getInitialValuesFromQuery(location.search);
    initialValues = {
      ...initialValues,
      email: username || initialValues.email || initialValues.username,
      ...location.state,
    };
    const workflowTypeValue = normalizeRegistrationWorkflowType(
      workflowType || location?.state?.workflowType || 'NewRegistration',
    );
    const isLinkingWorkflow = workflowTypeValue === WORKFLOW_TYPE_LINK_ACCOUNT;

    const customBrandingDisplayName = getCustomBrandingDetail(
      context,
      'displayName',
    )?.value;
    const isNewSignupWorkflowEnabled = isNewSignupWorkflow(context);

    useEffect(() => {
      const path = location.pathname;
      switch (path) {
        case '/account/claimaccount':
        case '/account/link/claimaccount':
          setRegistrationStep(0);
          break;
        case '/account/signup':
          setRegistrationStep(1);
          break;
        case '/account/confirm':
        case '/account/link/confirm':
          setRegistrationStep(2);
          break;
        case '/account/registration':
        case '/account/link/registration':
          setRegistrationStep(3);
          break;
        case '/account/success':
        case '/account/link/success':
          setRegistrationStep(4);
          break;
        case '/account/link/mobileprompt':
          setRegistrationStep(5);
          break;
        case '/account/selectprimarylogin':
          setRegistrationStep(6);
          break;
        default:
          setRegistrationStep(1);
          break;
      }
    }, [location.pathname]);

    if (user && user.isRegistered) {
      return <Redirect push to="/" />;
    }

    if (!isLinkingWorkflow && !isNewSignupWorkflowEnabled) {
      return (
        <Redirect
          push
          to={{
            pathname: '/signup',
            search: new URL(window.location.href).search,
          }}
        />
      );
    }

    const getTitle = (step) => {
      const titles = {
        0: 'Great, we found an hStream ID for:',
        1: 'Enter your email to get started',
        2: location.state?.phone ? 'Verify Your Code' : 'Verify Your Email',
        3: 'Enter your information below',
        5: 'Enter a mobile number for Account Recovery',
      };
      return titles[step];
    };

    const registrationStepComponentMap = {
      0: ClaimAccount,
      1: EmailValidation,
      2: VerificationCode,
      3: Registration,
      4: Success,
      5: MobilePromptMatchFound,
      6: SelectPrimaryLogin,
    };

    const navigate = (step, data) => {
      const effectiveWorkflowType = normalizeRegistrationWorkflowType(
        data?.workflowType ||
        location?.state?.workflowType ||
        workflowType ||
        'NewRegistration',
      );
      const isLinkingFlow = effectiveWorkflowType === WORKFLOW_TYPE_LINK_ACCOUNT;

      const page = {
        0: isLinkingFlow
          ? '/account/link/claimaccount'
          : '/account/claimaccount',
        1: '/account/signup',
        2: isLinkingFlow ? '/account/link/confirm' : '/account/confirm',
        3: isLinkingFlow
          ? '/account/link/registration'
          : '/account/registration',
        4: isLinkingFlow ? '/account/link/success' : '/account/success',
        5: '/account/link/mobileprompt',
        6: '/account/selectprimarylogin',
      };

      history.push({
        pathname: page[step],
        state: {
          ...location.state,
          ...data,
          workflowType: effectiveWorkflowType,
        },
        search: window.location.search,
      });
    };

    const RegisterStep = registrationStepComponentMap[registrationStep];

    // Check if mobile verification is included in this workflow
    const isMobileVerificationIncluded = useMemo(() => {
      return location.state?.phone && location.state?.isMobileRequired;
    }, [location.state?.phone, location.state?.isMobileRequired]);

    // Calculate total steps based on workflow configuration
    const totalSteps = useMemo(() => {
      if (isLinkingWorkflow) {
        return 2;
      }

      const params = new URLSearchParams(window.location.search);
      const isMobileRequired = isMobileNumberRequiredForLinking(context, params);
      const baseSteps = 3; // Email validation, verification code, registration

      return isMobileRequired ? baseSteps + 1 : baseSteps;
    }, [isLinkingWorkflow, context]);

    // Calculate which step number to display to the user
    const stepToRender = useMemo(() => {
      // Linking workflow shows step numbers differently
      if (isLinkingWorkflow) {
        return registrationStep - 1;
      }

      // Map internal step to display step number
      switch (registrationStep) {
        case registrationSteps.EMAILVALIDATION:
          return 1;
        case registrationSteps.VERIFICATIONCODE:
          // If mobile verification is included, this becomes step 4, otherwise step 2
          return isMobileVerificationIncluded ? 4 : 2;
        case registrationSteps.REGISTRATION:
          return 3;
        default:
          return registrationStep;
      }
    }, [registrationStep, isLinkingWorkflow, isMobileVerificationIncluded]);

    // Remove phone from registrationFields and initialValues when rendering MobilePromptMatchFound
    const registrationFieldsForStep =
      registrationStep === registrationSteps.MOBILEPROMPT
        ? { ...location.state, phone: undefined }
        : location.state;

    const initialValuesForStep =
      registrationStep === registrationSteps.MOBILEPROMPT
        ? { ...initialValues, phone: undefined, 'confirm-phone': undefined }
        : initialValues;

    return (
      <AuthWrapper classList={['SignUp']}>
        {!isLinkingWorkflow &&
          stepToRender <= totalSteps &&
          registrationStep !== registrationSteps.SUCCESS && (
            <div>
              Registration: Step {Math.max(1, stepToRender)} of {totalSteps}
            </div>
          )}
        <h2 className="AuthWrapper__register__title">
          {getTitle(registrationStep)}
        </h2>
        {customBrandingDisplayName &&
          (registrationStep === 1 || registrationStep === 3) && (
            <div className="AuthWrapper__redirect">
              to continue to {customBrandingDisplayName}
            </div>
          )}
        <RegisterStep
          navigate={navigate}
          disabled={isRequestPending}
          isRequestPending={isRequestPending}
          toSignIn={toSignIn}
          registrationFields={registrationFieldsForStep}
          initialValues={initialValuesForStep}
          PasswordComplexityFeature={PasswordComplexityFeature}
          {...location.state}
        />
      </AuthWrapper>
    );
  },
);

export const error = {
  errorFound: false,
  errorMessage: '',
  button: {
    text: '',
    link: '',
  },
};

export const setGenericError = (setError) => {
  setError((_error) => ({
    errorFound: true,
    errorMessage: errorMessages.processing.message,
  }));
};

Register.propTypes = {
  isRequestPending: PropTypes.bool,
  isPasswordVisible: PropTypes.bool,
  PasswordComplexityFeature: PropTypes.bool,
  togglePasswordVisibilityConnect: PropTypes.func.isRequired,
  user: PropTypes.shape({
    isRegistered: PropTypes.bool,
  }),
};

Register.defaultProps = {
  isPasswordVisible: false,
  PasswordComplexityFeature: false,
  isRequestPending: false,
  user: undefined,
};

const mapStateToProps = (state) => ({
  isRequestPending: selectors.isRequestPending(state),
  isPasswordVisible: selectors.isPasswordVisible(state),
  user: selectors.getUser(state),
});

const mapDispatchToProps = {
  signUpConnect: signUp,
  togglePasswordVisibilityConnect: togglePasswordVisibility,
};

const ConnectedRegister = connect(
  mapStateToProps,
  mapDispatchToProps,
)(Register);

export default ConnectedRegister;
export { Register as PureRegister };
